// client/src/pages/schedule/ScheduleCalendar.js
import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';

import { AuthContext } from '../../context/AuthContext.js';
import { getScheduleCalendar, getScheduleById } from '../../api/scheduleApi.js';

import ScheduleRead from './ScheduleRead';
import ScheduleForm from "./ScheduleForm";

import '../../css/calendar.css';
import './Schedule.css';

import meetingIcon from '../../image/icon_cal_meeting.gif';
import sharedIcon from '../../image/icon_cal_shared.gif';
import workIcon from '../../image/icon_cal_work.gif';
import allIcon from '../../image/icon_cal_all.gif';

function formatAMPM(date) {
  let hours = date.getHours();
  let minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  minutes = minutes < 10 ? '0' + minutes : minutes;
  return `${hours}:${minutes} ${ampm}`;
}

function ScheduleCalendar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [selectedRange, setSelectedRange] = useState(null);
  const { token, user } = useContext(AuthContext);
  const lastFetchKey = useRef('');
  const [currentParams, setCurrentParams] = useState({ viewtype: 'month' });
  const [eventType, setEventType] = useState('');

  // ✅ 모달 상태 관리
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [isReadOpen, setIsReadOpen] = useState(false);   // 조회 모달
  const [isFormOpen, setIsFormOpen] = useState(false);   // 수정 모달
  const [showScheduleForm, setShowScheduleForm] = useState(false); // 등록 모달

  // viewId 쿼리로 진입 시 모달 자동 오픈
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const viewId = params.get('viewId');
    if (!viewId) return;
    if (events.length === 0) {
      return;
    }
    const found = events.find(ev => String(ev.id) === String(viewId));
    if (found && found.extendedProps && found.extendedProps.schedule) {
      setSelectedSchedule(found.extendedProps.schedule);
      setIsReadOpen(true);
    } else if (!found) {
      // robust: 리스트에 없으면 단건 상세조회 시도
      (async () => {
        try {
          const res = await getScheduleById(viewId, token);
          if (res && res.id) {
            setSelectedSchedule(res);
            setIsReadOpen(true);
          } else {
            alert('존재하지 않는 일정입니다.');
            params.delete('viewId');
            navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
          }
        } catch (err) {
          alert('존재하지 않는 일정입니다.');
          params.delete('viewId');
          navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
        }
      })();
    }
  }, [location.search, location.pathname, events, token, navigate]);

  const queryParams = new URLSearchParams(location.search);
  const status = queryParams.get('status') || 'register';
  /*
  export const eventTypeMap = {
    W: '업무일정',
    S: '공유일정',
    M: '회의',
  };
  */
  useEffect(() => {
    const map = {
      work: 'W',
      share: 'S',
      meeting: 'M',
      all: 'all'
    };
    const searchParams = new URLSearchParams(location.search);
    const rawEventType = searchParams.get('eventtype') || 'all';
    setEventType(map[rawEventType] || 'all');
  }, [location.search]);

  const schedule_Title = () => {
    let title_Event = queryParams.get('eventtype') || 'all';
    let map = {
      work: '[업무]',
      share: '[공유]',
      meeting: '[회의]',
      all: ''
    };
    return map[title_Event] || "";
  };

  const calendarRef = useRef(null);

  const handleSelect = (info) => {
    const now = new Date();
    const currentTime = now.toTimeString().slice(0, 5);

    // endStr은 exclusive → 하루 빼주기
    const endDate = new Date(info.endStr);
    endDate.setDate(endDate.getDate() - 1);

    if (info.view.type === 'dayGridMonth') {
      setSelectedRange({
        start: `${info.startStr}T${currentTime}`,
        end: `${endDate.toISOString().slice(0, 10)}T${currentTime}`, // ✅ 하루 빼서 반영
        actionType: 'select'
      });
    } else {
      setSelectedRange({
        start: info.startStr,
        end: endDate.toISOString(), // ✅ 하루 빼서 반영
        actionType: 'select'
      });
    }
    setShowScheduleForm(true); // 작성 모달 열기
  };

  const handleDatesSet = (dateInfo) => {
    const viewTypeMap = {
      dayGridMonth: 'month',
      timeGridWeek: 'week',
      timeGridDay: 'day'
    };

    const viewtype = viewTypeMap[dateInfo.view.type] || 'month';
    const startDate = dateInfo.startStr.split('T')[0];
    const endDate = dateInfo.endStr.split('T')[0];

    const start = new Date(dateInfo.start);
    const year = start.getFullYear();
    const month = start.getMonth() + 1;
    const date = startDate;

    setCurrentParams({
      viewtype,
      year,
      month,
      startDate,
      endDate,
      date
    });
  };

  const fetchSchedule = useCallback(async (force = false) => {
    if (!token || !user?.userid) return;

    const currentKey = `${status}-${eventType || 'all'}-${token}-${user?.userid}-${currentParams.viewtype}-${currentParams.startDate}-${currentParams.endDate}-${currentParams.date}`;
    if (!force && lastFetchKey.current === currentKey) return;

    try {
      const data = await getScheduleCalendar(
        currentParams.viewtype,
        { status, eventType: eventType || 'all', ...currentParams },
        token
      );

      const schedules = Array.isArray(data) ? data : data?.schedules || [];
      const mapped = schedules.map(sch => {
        const start = new Date(`${sch.start_date}T${sch.start_time || "00:00:00"}`);
        let end = new Date(`${sch.end_date}T${sch.end_time || "23:59:00"}`);

        const timeLabel = start instanceof Date && !isNaN(start)
          ? formatAMPM(start)
          : "";

        const isAllDay = !sch.start_time && !sch.end_time;
        if (isAllDay && end instanceof Date && !isNaN(end)) {
          end.setDate(end.getDate() + 1);
        }

        let icon = "";
        let color = "#3788d8";
        switch (sch.event_type) {
          case "M":
            icon = `<img src="${meetingIcon}" style="width:14px;height:14px;vertical-align:middle;margin-right:4px;" />`;
            color = "#7535dc";
            break;
          case "S":
            icon = `<img src="${sharedIcon}" style="width:14px;height:14px;vertical-align:middle;margin-right:4px;" />`;
            color = "#ffc107";
            break;
          case "W":
            icon = `<img src="${workIcon}" style="width:14px;height:14px;vertical-align:middle;margin-right:4px;" />`;
            color = "#28a745";
            break;
          default:
            icon = "";
            color = "#3788d8";
        }

        return {
          id: sch.id,
          title: sch.start_time
            ? `${icon}(${timeLabel}) ${sch.title}`
            : `${icon}${sch.title}`,
          start,
          end,
          color,
          allDay: isAllDay,
          extendedProps: {
            schedule: sch,
            statusLabel: sch.status_label,
            eventType: sch.event_type
          }
        };
      });

      setEvents(mapped);
      lastFetchKey.current = currentKey;

    } catch (err) {
      console.error("[ScheduleCalendar] 일정 목록 불러오기 실패:", err);
      setEvents([]);
    }
  }, [status, eventType, token, user?.userid, currentParams]);

  useEffect(() => {
    fetchSchedule();
  }, [fetchSchedule]);

  return (
    <div className="schedule-calendar container-fluid py-4">
      <div className="card shadow-sm">
        <div className="card-body">
          {/* 상단 타이틀 및 버튼 */}
          <div
            className="d-flex justify-content-between align-items-center mb-3 p-3 rounded shadow"
            style={{ background: 'linear-gradient(90deg, #607d8b 0%, #cfd8dc 100%)', color: '#fff' }}
          >
            <h4 className="mb-0 fw-bold">{schedule_Title()} 일정 캘린더</h4>
            <div className="text-end">
              <div className="btn-group gap-1">
                <div className="target-schedule-menu">
                  <button className="btn btn-primary btn-sm">일정구분별</button>
                  <ul className="submenu">
                    <li>
                      <button onClick={() => navigate('/schedule/calendar')}>
                        <img
                          src={allIcon}
                          style={{ width: '14px', height: '14px', verticalAlign: 'middle', marginRight: '4px' }}
                          alt="업무일정 아이콘"
                        />

                        전체일정
                      </button>
                    </li>
                    <li>
                      <button onClick={() => navigate('/schedule/calendar?eventtype=work')}>
                        <img
                          src={workIcon}
                          style={{ width: '14px', height: '14px', verticalAlign: 'middle', marginRight: '4px' }}
                          alt="업무일정 아이콘"
                        />
                        업무일정
                      </button>
                    </li>

                    <li>
                      <button onClick={() => navigate('/schedule/calendar?eventtype=share')}>
                        <img
                          src={sharedIcon}
                          style={{ width: '14px', height: '14px', verticalAlign: 'middle', marginRight: '4px' }}
                          alt="공유일정 아이콘"
                        />
                        공유일정
                      </button>
                    </li>

                    <li>
                      <button onClick={() => navigate('/schedule/calendar?eventtype=meeting')}>
                        <img
                          src={meetingIcon}
                          style={{ width: '14px', height: '14px', verticalAlign: 'middle', marginRight: '4px' }}
                          alt="회의일정 아이콘"
                        />
                        회의일정
                      </button>
                    </li>
                  </ul>


                </div>
                <button
                  className="btn btn-success btn-sm fw-bold ms-0"
                  onClick={() => setShowScheduleForm(true)}
                >
                  <i className="bi bi-plus-circle"></i> 일정 등록
                </button>
              </div>
            </div>
          </div>

          <hr className="divider" />

          {/* FullCalendar */}
          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            locale="ko"
            selectable={true}
            selectMirror={true}
            slotEventOverlap={false}
            select={handleSelect}
            events={events}
            headerToolbar={{
              left: 'prev,next',
              center: 'title todayBtn',
              right: 'dayGridMonth,timeGridWeek,timeGridDay',
            }}
            footerToolbar={{
              left: 'prev,next',
              center: 'title todayBtn',
              right: 'dayGridMonth,timeGridWeek,timeGridDay',
            }}
            ref={calendarRef}
            customButtons={{
              todayBtn: {
                text: '오늘',
                click: () => {
                  const calendarApi = calendarRef.current.getApi();
                  calendarApi.today();
                },
              },
            }}
            height="auto"
            eventDisplay="block"
            themeSystem="bootstrap5"
            showNonCurrentDates={false}
            fixedWeekCount={false}
            datesSet={handleDatesSet}
            allDaySlot={true}
            dayMaxEventRows={5}
            dayMaxEvents={5}
            expandRows={true}
            slotDuration="00:30:00"
            selectAllow={(selectInfo) => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const startDate = new Date(selectInfo.start);
              startDate.setHours(0, 0, 0, 0);
              return startDate >= today;
            }}
            dayCellDidMount={(arg) => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const cellDate = new Date(arg.date);
              cellDate.setHours(0, 0, 0, 0);
              if (cellDate < today) {
                arg.el.style.backgroundColor = '#f0f0f0';
              }
            }}
            eventDidMount={(info) => {
              info.el.setAttribute(
                "title",
                info.event.title.replace(/<img[^>]*>/g, '').trim()
              );
              if (window.bootstrap) {
                new window.bootstrap.Tooltip(info.el, {
                  title: info.event.title.replace(/<img[^>]*>/g, '').trim(),
                  placement: 'top',
                  trigger: 'hover',
                  container: 'body'
                });
              }
            }}
            eventContent={(arg) => {
              const bgColor = arg.event.backgroundColor || arg.event.color || '#6c757d';
              return {
                html: `<div style="text-align:left; background-color:${bgColor}; color:#fff; border-radius:4px; padding:0px 4px;">
                        ${arg.event.title}
                      </div>`
              };
            }}
            eventClick={(info) => {
              setSelectedSchedule(info.event.extendedProps.schedule); // 모달에 넘길 데이터 세팅
              setIsReadOpen(true); // ✅ 조회 모달 열기
            }}
            eventMouseEnter={(info) => {
              info.el.parentElement?.classList.add('event-hover-active');
            }}
            eventMouseLeave={(info) => {
              info.el.parentElement?.classList.remove('event-hover-active');
            }}
          />

          {/* ✅ 일정 상세 모달 */}
          {isReadOpen && createPortal(
            <div className="schedule_backdrop">
              <div className="schedule_read_modal">
                <ScheduleRead
                  data={selectedSchedule}
                  onClose={(needRefresh) => {
                    setIsReadOpen(false);
                    setSelectedSchedule(null);
                    // robust: viewId 쿼리 정리
                    const params = new URLSearchParams(location.search);
                    if (params.has('viewId')) {
                      params.delete('viewId');
                      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
                    }
                    if (needRefresh) {
                      fetchSchedule(true); // ✅ 삭제/등록 후 캘린더 재조회
                    }
                  }}
                  onEdit={() => {
                    setIsReadOpen(false); // 조회 모달 닫기
                    setIsFormOpen(true);  // 수정 모달 열기
                  }}
                />
              </div>
            </div>,
            document.body
          )}

          {/* ✅ 일정 수정 모달 */}
          {isFormOpen && createPortal(
            <div className="schedule_backdrop">
              <div className="schedule_form_modal">
                <ScheduleForm
                  actionType="edit"
                  isModal={true}
                  schedule={selectedSchedule}
                  onClose={() => setIsFormOpen(false)}
                  onRefresh={() => {
                    setIsFormOpen(false);
                    // robust: viewId 쿼리 정리하여 저장 후 재오픈 방지
                    const params = new URLSearchParams(location.search);
                    if (params.has('viewId')) {
                      params.delete('viewId');
                      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
                    }
                    fetchSchedule(true); // 저장 후 캘린더 갱신
                  }}
                />
              </div>
            </div>,
            document.body
          )}

          {/* ✅ 일정 등록 모달 */}
          {showScheduleForm && createPortal(
            <div className="schedule_backdrop">
              <div className="schedule_form_modal">
                <ScheduleForm
                  actionType="create"
                  isModal={true}
                  initialData={selectedRange}   // ✅ 선택한 범위 전달
                  onClose={() => {
                    setShowScheduleForm(false);
                  }}
                  onRefresh={() => {
                    setShowScheduleForm(false);
                    fetchSchedule(true);
                  }}
                />
              </div>
            </div>,
            document.body
          )}
        </div>
      </div>
    </div>
  );
}

export default ScheduleCalendar;